const FITS = ["regular", "oversize"] as const;
const GENDERS = ["men", "women", "unisex"] as const;
const SIDES = ["front", "back"] as const;

function removeUrlFromSideValue(
  value: string | string[] | undefined,
  urlToRemove: string
): string | string[] | undefined {
  if (!value) return undefined;
  if (Array.isArray(value)) {
    const filtered = value.filter((url) => url !== urlToRemove);
    if (filtered.length === 0) return undefined;
    return filtered.length === 1 ? filtered[0] : filtered;
  }
  return value === urlToRemove ? undefined : value;
}

function removeUrlFromSideNode(
  node: Record<string, unknown>,
  urlToRemove: string,
  side?: "front" | "back"
): void {
  const sidesToClean = side ? [side] : SIDES;
  for (const sideKey of sidesToClean) {
    const next = removeUrlFromSideValue(
      node[sideKey] as string | string[] | undefined,
      urlToRemove
    );
    if (next === undefined) {
      delete node[sideKey];
    } else {
      node[sideKey] = next;
    }
  }
}

function removeUrlFromColorData(
  colorData: Record<string, unknown>,
  urlToRemove: string,
  side?: "front" | "back"
): void {
  removeUrlFromSideNode(colorData, urlToRemove, side);

  for (const fit of FITS) {
    const fitNode = colorData[fit];
    if (!fitNode || typeof fitNode !== "object") continue;

    for (const gender of GENDERS) {
      const genderNode = (fitNode as Record<string, unknown>)[gender];
      if (!genderNode || typeof genderNode !== "object") continue;
      removeUrlFromSideNode(genderNode as Record<string, unknown>, urlToRemove, side);
    }
  }
}

function pruneEmptyObjects(
  root: Record<string, unknown>,
  productId: string,
  colorKey: string
): void {
  const productNode = root[productId];
  if (!productNode || typeof productNode !== "object") return;

  const colorNode = (productNode as Record<string, unknown>)[colorKey];
  if (!colorNode || typeof colorNode !== "object") return;

  const colorRecord = colorNode as Record<string, unknown>;
  for (const fit of FITS) {
    const fitNode = colorRecord[fit];
    if (!fitNode || typeof fitNode !== "object") continue;

    for (const gender of GENDERS) {
      const genderNode = (fitNode as Record<string, unknown>)[gender];
      if (
        genderNode &&
        typeof genderNode === "object" &&
        Object.keys(genderNode).length === 0
      ) {
        delete (fitNode as Record<string, unknown>)[gender];
      }
    }

    if (Object.keys(fitNode as object).length === 0) {
      delete colorRecord[fit];
    }
  }

  if (Object.keys(colorRecord).length === 0) {
    delete (productNode as Record<string, unknown>)[colorKey];
  }

  if (Object.keys(productNode as object).length === 0) {
    delete root[productId];
  }
}

/** Remove a custom mockup URL from legacy and fit/gender nested structures. */
export function removeCustomMockupUrlFromTree(
  tree: Record<string, unknown>,
  productId: string,
  urlToRemove: string,
  options?: { color?: string; side?: "front" | "back" }
): void {
  const productNode = tree[productId];
  if (!productNode || typeof productNode !== "object") return;

  const colorKeys = options?.color
    ? [options.color]
    : Object.keys(productNode as object);

  for (const colorKey of colorKeys) {
    const colorData = (productNode as Record<string, unknown>)[colorKey];
    if (!colorData || typeof colorData !== "object") continue;

    removeUrlFromColorData(colorData as Record<string, unknown>, urlToRemove, options?.side);
    pruneEmptyObjects(tree, productId, colorKey);
  }
}
